import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Briefcase, Clock, Shield, Star, Ticket } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../lib/api-client';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog } from '../../components/ui/dialog';
import { useThemeStore } from '../../stores/theme-store';
import { PRIORITY_CHART_COLORS, PRIORITY_LABELS, STATUS_FLOW_ORDER, STATUS_LABELS, ALL_PRIORITIES } from '../../lib/constants';
import type { TicketPriority, TicketStatus } from '../../types';

interface DashboardData {
  totalTickets: number;
  openTickets: number;
  avgResolutionHours: number;
  slaBreaches: number;
  unassigned: number;
  resolvedToday: number;
  byStatus: { status: string; count: number }[];
  byPriority: { priority: string; count: number }[];
}

interface SlaData {
  compliance: number;
  breachCount: number;
  totalTickets: number;
}

interface CsatTicketRow {
  ticketId: string;
  numericId: number;
  formattedId: string;
  subject: string;
  clientName: string | null;
  status: string;
  ratedAt: string;
  comment: string | null;
}

interface CsatData {
  average: number;
  total: number;
  distribution: { score: number; count: number; tickets: CsatTicketRow[] }[];
}

interface WorkloadData {
  agents: { name: string; open: number; closed: number }[];
}

interface TpcAgentSlice {
  agentId: string | null;
  agentName: string | null;
  count: number;
}

interface TpcClientRow {
  clientElementId: number | null;
  clientName: string | null;
  total: number;
  agents: TpcAgentSlice[];
}

interface TpcData {
  clients: TpcClientRow[];
  total: number;
}

interface TpcUserRow {
  userId: string | null;
  userName: string | null;
  total: number;
  agents: TpcAgentSlice[];
}

interface TpcDrilldownData {
  clientElementId: number | null;
  clientName: string | null;
  total: number;
  users: TpcUserRow[];
}

const KPI_LINKS = {
  total: '/admin/tickets',
  open: '/admin/tickets?status=OPEN_GROUP',
  resolution: '/admin/tickets?status=RESOLVED',
  slaBreaches: '/admin/tickets',
  compliance: '/admin/tickets',
  csat: '/admin/tickets?status=CLOSED',
};

/** Pie slice color per priority — same hues as the priority tags (CRITIC red, BUG orange, NORMAL blue, ESTETIC slate, CHANGE_REQUEST purple) */
const PRIORITY_PIE_COLORS: Record<TicketPriority, { light: string; dark: string }> = PRIORITY_CHART_COLORS;

/** Sort status counts in workflow order (flow order, not alphabetical) */
function sortByFlowOrder(rows: { status: string; count: number }[]): { status: string; count: number }[] {
  return [...rows].sort((a, b) => {
    const ia = STATUS_FLOW_ORDER.indexOf(a.status as TicketStatus);
    const ib = STATUS_FLOW_ORDER.indexOf(b.status as TicketStatus);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

export function DashboardPage() {
  const dark = useThemeStore((s) => s.dark);
  const [csatDrillScore, setCsatDrillScore] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: () => api.get<DashboardData>('/reports/dashboard'),
  });
  const dash = data?.data;

  const { data: slaData } = useQuery({
    queryKey: ['reports', 'sla'],
    queryFn: () => api.get<SlaData>('/reports/sla'),
  });
  const sla = slaData?.data;

  const { data: csatData } = useQuery({
    queryKey: ['reports', 'csat'],
    queryFn: () => api.get<CsatData>('/reports/csat'),
  });
  const csat = csatData?.data;

  const { data: workloadData } = useQuery({
    queryKey: ['reports', 'workload'],
    queryFn: () => api.get<WorkloadData>('/reports/workload'),
  });
  const workload = workloadData?.data;

  const [tpcDrillClient, setTpcDrillClient] = useState<TpcClientRow | null>(null);
  const { data: tpcData } = useQuery({
    queryKey: ['reports', 'tickets-per-client'],
    queryFn: () => api.get<TpcData>('/reports/tickets-per-client'),
  });
  const tpc = tpcData?.data;

  const chartTheme = {
    axis: dark ? '#aebfd8' : '#4e5d77',
    text: dark ? '#edf2ff' : '#16233d',
    grid: dark ? 'rgba(169, 187, 219, 0.14)' : 'rgba(54, 74, 108, 0.12)',
    tooltipBg: dark ? 'rgba(28, 36, 56, 0.96)' : 'rgba(255, 255, 255, 0.97)',
    tooltipBorder: dark ? 'rgba(169, 187, 219, 0.2)' : 'rgba(54, 74, 108, 0.12)',
    primary: dark ? '#7ea4ff' : '#3556c6',
    accent: dark ? '#ff9a6b' : '#dd6a41',
    success: dark ? '#57d190' : '#1e9b61',
  };

  // Status volume in workflow order, with human-readable labels
  const byStatus = sortByFlowOrder(dash?.byStatus ?? []).map((s) => ({
    status: STATUS_LABELS[s.status as keyof typeof STATUS_LABELS] ?? s.status,
    count: s.count,
  }));

  // Priority mix in a stable, meaningful order (most urgent first)
  const byPriority = ALL_PRIORITIES
    .map((p) => ({ priority: p, count: dash?.byPriority?.find((x) => x.priority === p)?.count ?? 0 }))
    .filter((p) => p.count > 0);

  // Workload: every active agent, busiest first
  const agents = [...(workload?.agents ?? [])].sort((a, b) => (b.open + b.closed) - (a.open + a.closed));
  const workloadHeight = Math.max(280, agents.length * 44 + 70);

  // Tickets per client: top 10 clients by volume, stacked by assigned agent
  const tpcClients = (tpc?.clients ?? []).slice(0, 10);
  const tpcAgentNames = [...new Set(tpcClients.flatMap((c) => c.agents.map((a) => a.agentName).filter((n): n is string => n != null)))];
  const tpcChart = tpcClients.map((c) => {
    const row: Record<string, string | number | null> = {
      clientElementId: c.clientElementId,
      name: c.clientName ?? 'Fără client',
      total: c.total,
    };
    for (const a of c.agents) row[a.agentName ?? 'Nealocat'] = a.count;
    return row;
  });
  const tpcHeight = Math.max(280, tpcClients.length * 44 + 70);

  const tooltipStyle = {
    backgroundColor: chartTheme.tooltipBg,
    border: `1px solid ${chartTheme.tooltipBorder}`,
    borderRadius: 18,
    boxShadow: '0 18px 44px -28px rgba(15, 23, 42, 0.45)',
    color: chartTheme.text,
  };

  if (isLoading) {
    return (
      <div className="space-y-6 lg:space-y-8">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_340px]">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-48 w-full" />)}
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-[22rem] w-full" />
          <Skeleton className="h-[22rem] w-full" />
        </div>
      </div>
    );
  }

  const complianceClass = (sla?.compliance ?? 100) >= 90
    ? 'text-green-600 dark:text-green-400'
    : (sla?.compliance ?? 100) >= 70
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <div className="space-y-6 lg:space-y-8">
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_340px]">
        <div className="app-shell-panel rounded-[2rem] px-6 py-7 md:px-8 md:py-9">
          <p className="app-kicker">Encorsa operations overview</p>
          <h1 className="mt-4 text-[clamp(2.15rem,4vw,3.45rem)] font-semibold leading-[0.94] text-[color:var(--ink-strong)]">
            Dashboard operational pentru suport, claritate si SLA.
          </h1>
          <p className="mt-4 max-w-[62ch] text-sm text-[color:var(--ink-muted)] md:text-base">
            Vezi imediat volumul activ, presiunea din pipeline si perceptia clientilor fara sa sari intre mai multe rapoarte.
          </p>
        </div>

        <div className="app-shell-panel rounded-[2rem] px-6 py-6">
          <p className="app-kicker">Semnal rapid</p>
          <div className="mt-5 space-y-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-sm text-[color:var(--ink-muted)]">Tichete fara alocare</p>
                <p className="mt-1 text-3xl font-semibold tracking-[-0.05em] text-[color:var(--ink-strong)]">{dash?.unassigned ?? 0}</p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">acum</span>
            </div>
            <div className="h-px bg-[color:var(--surface-line)]" />
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-sm text-[color:var(--ink-muted)]">Rezolvate azi</p>
                <p className="mt-1 text-3xl font-semibold tracking-[-0.05em] text-[color:var(--ink-strong)]">{dash?.resolvedToday ?? 0}</p>
              </div>
              <span className="rounded-full bg-[var(--surface-panel-strong)] px-3 py-1 text-xs font-medium text-[color:var(--ink-muted)]">
                ritm curent
              </span>
            </div>
            <div className="h-px bg-[color:var(--surface-line)]" />
            <p className="text-sm leading-6 text-[color:var(--ink-muted)]">
              Lista de tichete ramane punctul de control operational. KPI-urile de mai jos duc direct acolo pentru verificare si actiune.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Link className="metric-link" to={KPI_LINKS.total}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Portofoliu</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">Tichete totale</p>
            </div>
            <span className="metric-link__icon"><Ticket size={18} className="text-primary" /></span>
          </div>
          <p className="metric-link__value">{dash?.totalTickets ?? 0}</p>
          <div className="metric-link__foot">
            <span>Baza completa a solicitarilor din sistem.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>

        <Link className="metric-link" to={KPI_LINKS.open}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Flux activ</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">Tichete deschise</p>
            </div>
            <span className="metric-link__icon"><Briefcase size={18} className="text-[color:var(--color-accent)]" /></span>
          </div>
          <p className="metric-link__value">{dash?.openTickets ?? 0}</p>
          <div className="metric-link__foot">
            <span>Toate tichetele care nu sunt inca inchise.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>

        <Link className="metric-link" to={KPI_LINKS.resolution}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Eficienta</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">Ore rezolvare medie</p>
            </div>
            <span className="metric-link__icon"><Clock size={18} className="text-[color:var(--color-success)]" /></span>
          </div>
          <p className="metric-link__value">{dash?.avgResolutionHours?.toFixed(1) ?? '—'}</p>
          <div className="metric-link__foot">
            <span>Media calculata pe tichetele deja rezolvate.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>

        <Link className="metric-link" to={KPI_LINKS.slaBreaches}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Atentie</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">Brese SLA</p>
            </div>
            <span className="metric-link__icon"><AlertTriangle size={18} className="text-[color:var(--color-warning)]" /></span>
          </div>
          <p className="metric-link__value">{dash?.slaBreaches ?? 0}</p>
          <div className="metric-link__foot">
            <span>Cazuri care cer verificare si triere rapida.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>

        <Link className="metric-link" to={KPI_LINKS.compliance}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Disciplina</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">Conformitate SLA</p>
            </div>
            <span className="metric-link__icon"><Shield size={18} className="text-primary" /></span>
          </div>
          <p className={`metric-link__value ${complianceClass}`}>{sla?.compliance?.toFixed(1) ?? '—'}%</p>
          <div className="metric-link__foot">
            <span>Starea generala a termenelor din contracte.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>

        <Link className="metric-link" to={KPI_LINKS.csat}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="metric-link__eyebrow">Perceptie</p>
              <p className="mt-3 text-base font-semibold text-[color:var(--ink-strong)]">CSAT mediu</p>
            </div>
            <span className="metric-link__icon"><Star size={18} className="text-[color:var(--color-accent)]" /></span>
          </div>
          <p className="metric-link__value text-primary">{csat?.average?.toFixed(1) ?? '—'}/5</p>
          <div className="metric-link__foot">
            <span>Scorul venit din tichetele inchise de clienti.</span>
            <ArrowUpRight size={16} className="shrink-0 text-primary" />
          </div>
        </Link>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="chart-panel overflow-hidden">
          <div className="flex items-start justify-between gap-4 border-b border-[color:var(--surface-line)] px-6 py-5">
            <div>
              <p className="app-kicker">Pipeline</p>
              <h2 className="mt-2 text-2xl font-semibold text-[color:var(--ink-strong)]">Volum pe status</h2>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Unde se aduna presiunea operationala in acest moment.</p>
            </div>
            <Link to="/admin/tickets" className="rounded-full border border-[color:var(--surface-line)] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--ink-muted)] hover:border-[color:var(--surface-line-strong)] hover:text-[color:var(--ink-strong)]">
              Vezi tichete
            </Link>
          </div>
          <div className="h-80 px-4 pb-4 md:px-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byStatus} margin={{ top: 18, right: 8, left: -12, bottom: 8 }}>
                <CartesianGrid vertical={false} stroke={chartTheme.grid} />
                <XAxis dataKey="status" tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={{ stroke: chartTheme.grid }} tickLine={{ stroke: chartTheme.grid }} />
                <YAxis tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: chartTheme.grid }} />
                <Bar dataKey="count" fill={chartTheme.primary} radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-panel overflow-hidden">
          <div className="flex items-start justify-between gap-4 border-b border-[color:var(--surface-line)] px-6 py-5">
            <div>
              <p className="app-kicker">Mix de cereri</p>
              <h2 className="mt-2 text-2xl font-semibold text-[color:var(--ink-strong)]">Prioritati in portofoliu</h2>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Raportul dintre urgente, suport uzual si cereri de schimbare.</p>
            </div>
          </div>
          <div className="flex h-80 items-center justify-center px-4 pb-4 md:px-6">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byPriority} dataKey="count" nameKey="priority" cx="50%" cy="50%" outerRadius={88} innerRadius={48} label={false}>
                  {byPriority.map((entry) => (
                    <Cell key={entry.priority} fill={PRIORITY_PIE_COLORS[entry.priority as TicketPriority]?.[dark ? 'dark' : 'light'] ?? chartTheme.primary} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value, PRIORITY_LABELS[name as TicketPriority] ?? name]} />
                <Legend wrapperStyle={{ color: chartTheme.axis, fontSize: '12px', paddingTop: '8px' }} formatter={(value: string) => PRIORITY_LABELS[value as TicketPriority] ?? value} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="chart-panel overflow-hidden">
          <div className="flex items-start justify-between gap-4 border-b border-[color:var(--surface-line)] px-6 py-5">
            <div>
              <p className="app-kicker">Distribuire</p>
              <h2 className="mt-2 flex items-center gap-2 text-2xl font-semibold text-[color:var(--ink-strong)]"><Briefcase size={18} /> Workload agenți</h2>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Comparație între portofoliul deschis si volumele deja inchise per agent.</p>
            </div>
          </div>
          <div className="px-4 pb-4 md:px-6" style={{ height: workloadHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={agents} layout="vertical" margin={{ top: 18, right: 8, left: 12, bottom: 8 }}>
                <CartesianGrid horizontal={false} stroke={chartTheme.grid} />
                <XAxis type="number" tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={{ stroke: chartTheme.grid }} tickLine={{ stroke: chartTheme.grid }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: chartTheme.grid }} />
                <Legend wrapperStyle={{ color: chartTheme.axis, fontSize: '12px' }} />
                <Bar dataKey="open" fill={chartTheme.accent} name="Deschise" radius={[0, 8, 8, 0]} stackId="a" />
                <Bar dataKey="closed" fill={chartTheme.success} name="Închise" radius={[0, 8, 8, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-panel overflow-hidden">
          <div className="flex items-start justify-between gap-4 border-b border-[color:var(--surface-line)] px-6 py-5">
            <div>
              <p className="app-kicker">Feedback</p>
              <h2 className="mt-2 flex items-center gap-2 text-2xl font-semibold text-[color:var(--ink-strong)]"><Star size={18} /> Satisfacție clienți</h2>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Distribuția scorurilor venite din tichetele care au trecut prin feedback. Click pe o bară pentru tichetele respective.</p>
            </div>
            {csat && <span className="text-3xl font-semibold tracking-[-0.05em] text-primary">{csat.average?.toFixed(1)}/5</span>}
          </div>
          <div className="h-80 px-4 pb-4 md:px-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={csat?.distribution ?? []} margin={{ top: 18, right: 8, left: -12, bottom: 8 }}>
                <CartesianGrid vertical={false} stroke={chartTheme.grid} />
                <XAxis dataKey="score" tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={{ stroke: chartTheme.grid }} tickLine={{ stroke: chartTheme.grid }} />
                <YAxis tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: chartTheme.grid }} />
                <Bar
                  dataKey="count"
                  fill={chartTheme.primary}
                  radius={[10, 10, 0, 0]}
                  cursor="pointer"
                  onClick={(entry: { score?: number }) => {
                    const score = entry?.score;
                    if (typeof score === 'number' && (csat?.distribution ?? []).some((d) => d.score === score && d.count > 0)) {
                      setCsatDrillScore(score);
                    }
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section>
        <div className="chart-panel overflow-hidden">
          <div className="flex items-start justify-between gap-4 border-b border-[color:var(--surface-line)] px-6 py-5">
            <div>
              <p className="app-kicker">Volum pe clienți</p>
              <h2 className="mt-2 flex items-center gap-2 text-2xl font-semibold text-[color:var(--ink-strong)]"><Ticket size={18} /> Tichete per client</h2>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)]">
                Top {tpcClients.length || 10} clienți după volum, cu distribuția pe agenții alocați. Click pe un client pentru drill-down pe utilizatori.
              </p>
            </div>
          </div>
          {tpcClients.length === 0 ? (
            <div className="flex h-64 items-center justify-center px-6">
              <p className="text-sm text-[color:var(--ink-muted)]">Nu există tichete de grupat.</p>
            </div>
          ) : (
            <div className="px-4 pb-4 md:px-6" style={{ height: tpcHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tpcChart} layout="vertical" margin={{ top: 18, right: 8, left: 12, bottom: 8 }}>
                  <CartesianGrid horizontal={false} stroke={chartTheme.grid} />
                  <XAxis type="number" tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={{ stroke: chartTheme.grid }} tickLine={{ stroke: chartTheme.grid }} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: chartTheme.grid }} />
                  <Legend wrapperStyle={{ color: chartTheme.axis, fontSize: '12px' }} />
                  {tpcAgentNames.map((agentName, i) => (
                    <Bar
                      key={agentName}
                      dataKey={agentName}
                      name={agentName}
                      stackId="tpc"
                      fill={TPC_AGENT_COLORS[i % TPC_AGENT_COLORS.length]}
                      radius={i === tpcAgentNames.length - 1 ? [0, 8, 8, 0] : undefined}
                      cursor="pointer"
                      onClick={(entry: { clientElementId?: number | null }) => {
                        const cid = entry?.clientElementId;
                        const client = tpcClients.find((c) => c.clientElementId === cid);
                        if (client) setTpcDrillClient(client);
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <CsatDrilldownDialog
        open={csatDrillScore !== null}
        score={csatDrillScore}
        tickets={csat?.distribution.find((d) => d.score === csatDrillScore)?.tickets ?? []}
        onClose={() => setCsatDrillScore(null)}
      />

      <TicketsPerClientDrilldownDialog
        client={tpcDrillClient}
        onClose={() => setTpcDrillClient(null)}
      />
    </div>
  );
}

/** Stacked-bar palette for the per-client agent distribution (stable, distinguishable). */
const TPC_AGENT_COLORS = ['#3556c6', '#dd6a41', '#1e9b61', '#b45309', '#7c3aed', '#0e7490', '#be185d', '#4d7c0f'];

/** Drill-down: tickets per client user (creator), stacked by assigned agent. */
function TicketsPerClientDrilldownDialog({
  client,
  onClose,
}: {
  client: TpcClientRow | null;
  onClose: () => void;
}) {
  const open = client !== null;
  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'tickets-per-client', client?.clientElementId ?? 'null', 'drilldown'],
    queryFn: () => api.get<TpcDrilldownData>(`/reports/tickets-per-client/${client?.clientElementId ?? 'null'}/drilldown`),
    enabled: open,
  });
  const drill = data?.data;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={client ? `Tichete per utilizator — ${client.clientName ?? 'Fără client'}` : 'Tichete per utilizator'}
      className="max-w-3xl"
    >
      {isFetching || !drill ? (
        <div className="flex justify-center py-10">
          <Skeleton className="h-6 w-40" />
        </div>
      ) : drill.users.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Nu există tichete pentru acest client.
        </p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="py-2 pr-3 font-semibold">Utilizator client</th>
                <th className="py-2 pr-3 font-semibold">Tichete</th>
                <th className="py-2 font-semibold">Agenți alocați</th>
              </tr>
            </thead>
            <tbody>
              {drill.users.map((u) => (
                <tr key={u.userId ?? 'none'} className="border-b border-gray-100 align-top last:border-0 dark:border-gray-800">
                  <td className="whitespace-nowrap py-2.5 pr-3 font-medium text-gray-900 dark:text-gray-100">
                    {u.userName ?? '—'}
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-gray-600 dark:text-gray-300">{u.total}</td>
                  <td className="py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {u.agents.map((a) => (
                        <span
                          key={a.agentId ?? 'none'}
                          className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                        >
                          {a.agentName ?? 'Nealocat'} · {a.count}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

/** Drill-down: the tickets behind one CSAT score (opened by clicking a bar). */
function CsatDrilldownDialog({
  open,
  score,
  tickets,
  onClose,
}: {
  open: boolean;
  score: number | null;
  tickets: CsatTicketRow[];
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={score != null ? `Tichete cu nota ${score}/5` : 'Tichete CSAT'}
      className="max-w-3xl"
    >
      {tickets.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Nu există tichete cu acest scor.
        </p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="py-2 pr-3 font-semibold">ID</th>
                <th className="py-2 pr-3 font-semibold">Subiect</th>
                <th className="py-2 pr-3 font-semibold">Client</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 pr-3 font-semibold">Data</th>
                <th className="py-2 font-semibold">Comentariu</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.ticketId} className="border-b border-gray-100 align-top last:border-0 dark:border-gray-800">
                  <td className="whitespace-nowrap py-2.5 pr-3 font-mono text-xs text-gray-500 dark:text-gray-400">{t.formattedId}</td>
                  <td className="py-2.5 pr-3">
                    <Link to={`/admin/tickets/${t.ticketId}`} className="font-medium text-primary hover:underline">
                      {t.subject}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-gray-600 dark:text-gray-300">{t.clientName ?? '—'}</td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-gray-600 dark:text-gray-300">
                    {STATUS_LABELS[t.status as keyof typeof STATUS_LABELS] ?? t.status}
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-gray-600 dark:text-gray-300">
                    {new Date(t.ratedAt).toLocaleDateString('ro-RO')}
                  </td>
                  <td className="py-2.5 text-gray-600 dark:text-gray-300">{t.comment ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
